import { Button, Modal, Steps, Upload } from 'antd';
import React, { useEffect, useState } from 'react';
import { Bounce, toast } from 'react-toastify';
import useAuthRequest from '../../../services/requests.service';
import { useNavigate } from 'react-router-dom';
import { PlusOutlined } from '@ant-design/icons';
import { useImageUpload } from '../../../hooks/useImageUpload';
import FormManagementCompany from './Form/FormManagementCompany';

const { confirm } = Modal;

const Infos = ({ page }) => {
    const { patchDataAPI } = useAuthRequest();
    const [current, setCurrent] = useState(0);
    const [formData, setFormData] = useState({ ...page });
    const [imgList, setImgList] = useState(page.avatar ? [{ url: page.avatar }] : []);
    const [selectedImage, setSelectedImage] = useState(null);
    const { uploadImage, isUploading } = useImageUpload();
    const request = useAuthRequest();
    const navigate = useNavigate();

    useEffect(() => {
        setFormData({ ...page });
    }, [page]);

    const onChange = (value) => {
        if (value < current) {
            setCurrent(value);
        }
    };

    const steps = [
        { title: 'Informations 1/3' },
        { title: 'Informations 2/3' },
        { title: 'Informations 3/3' },
    ];

    const handleDeleteAccount = () => {
        confirm({
            title: 'Êtes-vous sûr de vouloir supprimer cette page?',
            content: 'Cette action est irréversible',
            okText: 'Oui',
            okType: 'danger',
            cancelText: 'Non',
            centered: true,
            async onOk() {
                if (page.subscription) {
                    toast.error("Vous avez un abonnement en cours, veuillez l'annuler avant de supprimer cette page!", {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        theme: 'light',
                        transition: Bounce,
                    });
                    return;
                }

                try {
                    const res = await request.deleteDataAPI(`pagePromo/${page._id}`);
                    toast.success(res.msg, {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        theme: 'light',
                        transition: Bounce,
                    });
                    navigate('/dashboard');
                } catch (e) {
                    toast.error(e.msg, {
                        position: 'top-right',
                        autoClose: 5000,
                        hideProgressBar: false,
                        closeOnClick: true,
                        pauseOnHover: true,
                        draggable: true,
                        theme: 'light',
                        transition: Bounce,
                    });
                }
            },
        });
    };

    const beforeUpload = (file) => {
        const isJpgOrPng = file.type === 'image/jpeg' || file.type === 'image/png';
        const isLessThan3MB = file.size / 1024 / 1024 < 3;

        if (!isJpgOrPng) {
            toast.error('Le fichier doit être au format JPG ou PNG.');
            return Upload.LIST_IGNORE;
        }

        if (!isLessThan3MB) {
            toast.error('La taille du fichier ne doit pas dépasser 3MB.');
            return Upload.LIST_IGNORE;
        }

        return true;
    };

    const onFileChange = ({ fileList }) => {
        setImgList(fileList);
        if (fileList.length > 0) {
            setSelectedImage(fileList[0].originFileObj);
        } else {
            setSelectedImage(null);
        }
    };

    const customRequest = ({ onSuccess }) => {
        setTimeout(() => {
            onSuccess("ok");
        }, 0);
    };

    const handleUpdateLogo = async () => {
        try {
            const imageUrl = await uploadImage(selectedImage);

            if (imageUrl) {
                const res = await patchDataAPI(`/page/${page._id}`, { avatar: imageUrl });
                toast.success('Logo modifié avec succès', {
                    position: 'top-right',
                    autoClose: 5000,
                    hideProgressBar: false,
                    closeOnClick: true,
                    pauseOnHover: true,
                    draggable: true,
                    theme: 'light',
                });
            }
        } catch (e) {
            console.error('Error updating logo:', e);
            toast.error("Erreur lors de la modification du logo", {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: 'light',
            });
        }
    };

    const handleFormSave = async (values) => {
        try {
            const res = await patchDataAPI(`/page/${page._id}`, values);
            toast.success('Informations mises à jour avec succès', {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: 'light',
            });
            // Optionnel : mettre à jour les données locales
            setFormData({ ...formData, ...values });
        } catch (e) {
            console.error('Error updating page info:', e);
            toast.error("Erreur lors de la mise à jour des informations", {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                theme: 'light',
            });
        }
    };

    return (
        <div className="flex flex-col">
            <h2 className="text-xl text-slate-800 dark:text-slate-100 font-bold mb-8">Informations</h2>

            <Steps
                type="navigation"
                current={current}
                onChange={onChange}
                className="site-navigation-steps mb-12"
            >
                {steps.map((step) => (
                    <Steps.Step key={step.title} title={step.title} />
                ))}
            </Steps>

            <div className="w-2/3 mx-auto">
                <FormManagementCompany 
                    page={page}
                    onSave={handleFormSave}
                    steps={steps}
                    current={current}
                    onStepChange={setCurrent}
                />
            </div>

            <section className="mt-6">
                <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">Modifier Logo</h2>

                <div className='mt-4'>
                    <Upload
                        beforeUpload={beforeUpload}
                        onChange={onFileChange}
                        customRequest={customRequest}
                        fileList={imgList}
                        listType="picture-card"
                        maxCount={1}
                    >
                        {imgList.length >= 1 ? null : (
                            <div>
                                <PlusOutlined />
                                <div style={{ marginTop: 8 }}>Télécharger</div>
                            </div>
                        )}
                    </Upload>
                </div>
                <div className="mt-5">
                    <Button 
                        size="large" 
                        onClick={handleUpdateLogo}
                        loading={isUploading}
                    >
                        {isUploading ? 'Modification...' : 'Modifier'}
                    </Button>
                </div>
            </section>

            <section className="mt-6">
                <h2 className="text-xl leading-snug text-slate-800 dark:text-slate-100 font-bold mb-1">Page</h2>
                <div className="text-sm">Vous pouvez supprimer votre page.</div>
                <div className="mt-5">
                    <Button size="large" danger onClick={handleDeleteAccount}>
                        Supprimer page
                    </Button>
                </div>
            </section>
        </div>
    );
};

export default Infos;